import { useState, type RefObject } from 'react';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import { Beef } from '@hungryroot/icons';

import { Toggle, ToggleFour, ToggleVariant } from '../../main';
import { type QuizTagKeys } from '../../quiz-hooks';
import {
  type QuizPreferenceItem,
  tagAnalytics,
} from '../../segment/quizAnalytics';
import {
  ControlPreferenceOptionSelect,
  type Props as ControlPreferenceOptionSelectProps,
} from '../ControlPreferenceOptionSelect/ControlPreferenceOptionSelect';
import {
  QuizQuestionScreen,
  type Props as QuizQuestionScreenProps,
} from '../QuizQuestionScreen/QuizQuestionScreen';
import styles from './PreferenceOptionsPage.module.css';

type Props<K extends QuizTagKeys> = {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
  toggleType?: ToggleVariant | 'toggleFour';
  layoutVariant?: ControlPreferenceOptionSelectProps<K>['layoutVariant'];
  showRemark?: boolean;
  textWrapperClass?: string;
  trackingEventName?: string;
} & Pick<QuizQuestionScreenProps, 'heroText' | 'subCopy'> &
  Pick<
    ControlPreferenceOptionSelectProps<K>,
    'ariaLabel' | 'preferenceOptionKey' | 'question' | 'tagExclusions'
  >;

export function PreferenceOptionsPage<K extends QuizTagKeys>(props: Props<K>) {
  const {
    testid,
    heroText,
    subCopy,
    ariaLabel,
    preferenceOptionKey,
    question,
    toggleType = ToggleVariant.One,
    layoutVariant = 'one-column-to-two-column',
    showRemark = false,
    tagExclusions = undefined,
    textWrapperClass,
    scrollRef,
    trackingEventName,
  } = props;
  const [trackedItems, setTrackedItems] = useState<QuizPreferenceItem[]>([]);

  useScrollToTopFixIOS(scrollRef);

  return (
    <QuizQuestionScreen
      testid={testid}
      heroText={heroText}
      subCopy={subCopy}
      onSubmitAnalytics={() => {
        trackingEventName && tagAnalytics(trackingEventName, trackedItems);
      }}
    >
      <ControlPreferenceOptionSelect
        ariaLabel={ariaLabel}
        preferenceOptionKey={preferenceOptionKey}
        question={question}
        layoutVariant={layoutVariant}
        defaultValue={[]}
        tagExclusions={tagExclusions}
        onTrackSelected={setTrackedItems}
      >
        {({ active, tag, index }) => {
          const { id: tagId, name: tagName } = tag;

          if (toggleType === 'toggleFour') {
            return (
              <ToggleFour
                textWrapperClass={textWrapperClass}
                active={active}
                key={tagId}
                remark={showRemark ? tag.remark : undefined}
                testid={`${testid}-toggle-tag-${tagId}`}
                tagId={tagId}
                tagName={tagName}
                imgSrc={tag.image_url}
                style={{ animationDelay: `${0.05 * index + 0.5}s` }}
              />
            );
          }

          return (
            <Toggle
              active={active}
              testid={`${testid}-toggle-tag-${tagId}`}
              subcopy={showRemark ? tag.remark : undefined}
              label={tagName}
              variant={toggleType}
              illustration={
                <div className={styles['illustration-36-36']}>
                  {tag.thumbnail_url ? (
                    <img src={tag.thumbnail_url} alt={tag.name} />
                  ) : (
                    <Beef />
                  )}
                </div>
              }
              style={{ animationDelay: `${0.05 * index + 0.5}s` }}
            />
          );
        }}
      </ControlPreferenceOptionSelect>
    </QuizQuestionScreen>
  );
}
